import React, { useState } from 'react';

import type { ApiClient } from '../api/client';
import { PreviewDialog } from './PreviewDialog';
import { ConversationCreateForm } from './ConversationCreateForm';
import { ConversationTable } from './ConversationTable';
import type {
  AppItem,
  Conversation,
  IdentityHealthResponse,
  IdentityServiceSummary,
  OpsHistoryResponse,
  OpsOverview,
  Pipeline,
  PipelineJob,
  UnifiedOpsPreviewResponse
} from '../types';

export function StageTabs({
  tabs,
  selectedId,
  onSelect
}: {
  tabs: { id: string; label: string }[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="tabs">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          className={tab.id === selectedId ? 'active' : ''}
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function StageCreateForm({
  onCreate,
  disabled
}: {
  onCreate: (name: string, gitlabEnvironmentName?: string) => Promise<boolean>;
  disabled: boolean;
}) {
  const [name, setName] = useState('');
  const [gitlabEnvironmentName, setGitlabEnvironmentName] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || busy || disabled) {
      return;
    }
    setBusy(true);
    const created = await onCreate(name.trim(), gitlabEnvironmentName.trim() || undefined);
    if (created) {
      setName('');
      setGitlabEnvironmentName('');
    }
    setBusy(false);
  };

  return (
    <form onSubmit={handleSubmit} className="stack">
      <label>
        <span>Stage name</span>
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Stage name (e.g., dev, staging)"
          disabled={busy || disabled}
        />
      </label>
      <label>
        <span>GitLab environment name (enables generated CI deployment)</span>
        <input
          type="text"
          value={gitlabEnvironmentName}
          onChange={(event) => setGitlabEnvironmentName(event.target.value)}
          placeholder="e.g., review/123, production, staging"
          disabled={busy || disabled}
        />
        <span className="hint">
          Leave empty to keep this stage out of generated GitLab CI deployment.
        </span>
      </label>
      <div className="row" style={{ justifyContent: 'flex-end', width: '100%' }}>
        <button type="submit" className="primary" disabled={!name.trim() || busy || disabled}>
          {busy ? 'Creating...' : 'Create stage'}
        </button>
      </div>
    </form>
  );
}

export function GitlabCiPreviewDialog({
  open,
  preview,
  instructions,
  onClose
}: {
  open: boolean;
  preview: { original: string | null; updated: string } | null;
  instructions: string | null;
  onClose: () => void;
}) {
  if (!open || !preview) {
    return null;
  }

  return (
    <PreviewDialog
      title="GitLab CI Config Preview"
      tabs={[
        { id: 'updated', label: 'Updated .gitlab-ci.yml', content: preview.updated },
        ...(preview.original
          ? [{ id: 'original', label: 'Original', content: preview.original }]
          : []),
        ...(instructions
          ? [{ id: 'instructions', label: 'What to do', content: instructions }]
          : [])
      ]}
      open={open}
      onClose={onClose}
      showCopy={true}
    />
  );
}

export function StageCreateDialog({
  open,
  disabled,
  onClose,
  onCreate
}: {
  open: boolean;
  disabled: boolean;
  onClose: () => void;
  onCreate: (name: string, gitlabEnvironmentName?: string) => Promise<boolean>;
}) {
  if (!open) {
    return null;
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog" style={{ maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>Create Stage</h3>
          <button type="button" className="ghost" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="dialog-content">
          <StageCreateForm onCreate={onCreate} disabled={disabled} />
        </div>
      </div>
    </div>
  );
}

export function UnifiedOpsPreviewDialog({
  open,
  preview,
  selectedFiles,
  applying,
  hasMissingSecrets,
  missingSecretKeys,
  identityServices,
  identityServicesBusy,
  ymlIdConfigured,
  identityHealth,
  identityHealthBusy,
  onClose,
  onApply,
  onToggleAll,
  onToggleFile,
  onGenerateMissing,
  onImportDeployedSecrets,
  onGenerateSecret,
  onSetSecret,
  onReconcileIdentity,
  onManualIdentitySecrets,
  onCheckIdentityHealth
}: {
  open: boolean;
  preview: UnifiedOpsPreviewResponse | null;
  selectedFiles: Set<string>;
  applying: boolean;
  hasMissingSecrets: boolean;
  missingSecretKeys: string[];
  identityServices: IdentityServiceSummary[];
  identityServicesBusy: boolean;
  ymlIdConfigured: boolean;
  identityHealth: IdentityHealthResponse | null;
  identityHealthBusy: boolean;
  onClose: () => void;
  onApply: () => void | Promise<void>;
  onToggleAll: (checked: boolean) => void;
  onToggleFile: (path: string, checked: boolean) => void;
  onGenerateMissing: () => void | Promise<void>;
  onImportDeployedSecrets: (target: 'stage' | 'general') => void | Promise<void>;
  onGenerateSecret: (key: string) => void | Promise<void>;
  onSetSecret: (key: string, value: string) => void | Promise<void>;
  onReconcileIdentity: (serviceName: string) => void | Promise<void>;
  onManualIdentitySecrets: (serviceName: string, clientId: string, clientSecret: string) => void | Promise<void>;
  onCheckIdentityHealth: () => void | Promise<void>;
}) {
  if (!open || !preview || !preview.files) {
    return null;
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog" style={{ maxWidth: '900px' }} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>Unified Deploy Preview - {preview.stage_name}</h3>
          <button type="button" className="ghost" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="dialog-content" style={{ maxHeight: '60vh', overflow: 'auto' }}>
          <div className="stack">
            <p className="status">
              Select files to apply. Files showing a diff will be updated on the server.
            </p>

            {preview.restore_lineage && (
              <div className="card" style={{ margin: 0, marginBottom: '1rem' }}>
                <strong>Restore regeneration</strong>
                <p className="status">
                  This preview is based on faithful backup restore <span className="mono">{preview.restore_lineage}</span>.
                  Applying creates a separate target-stage regeneration commit, including an empty commit when the rendered files already match.
                </p>
              </div>
            )}

            {(preview.identity_refs?.length ?? 0) > 0 && (
              <div className="card" style={{ margin: 0, marginBottom: '1rem' }}>
                <div className="card-header"><strong>Resolved Identity References</strong></div>
                <div className="card-body" style={{ padding: '0.5rem' }}>
                  {preview.identity_refs?.map((identity) => (
                    <div key={`${identity.stage_id ?? 'production'}:${identity.service_name}`} style={{ marginBottom: '0.75rem' }}>
                      <div>
                        <strong className="mono">{identity.service_name}</strong>{' '}
                        <span style={{ color: identity.valid ? 'green' : '#dc3545' }}>
                          {identity.valid ? 'validated' : 'blocked'}
                        </span>
                      </div>
                      <div className="hint mono">
                        app={identity.app_slug} stage={identity.stage_name} namespace={identity.namespace ?? 'missing'}
                      </div>
                      {identity.scopes.map((scope) => (
                        <div className="hint mono" key={scope.key}>
                          scope.{scope.key}={scope.name} resource={scope.resource ?? 'missing'}
                        </div>
                      ))}
                      {Object.entries(identity.client_scopes).map(([client, scopes]) => (
                        <div className="hint mono" key={client}>client.{client}.scopes={scopes}</div>
                      ))}
                      {identity.issues.map((issue) => (
                        <div key={issue} style={{ color: '#dc3545' }}>{issue}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {identityServices.length > 0 && (
              <div
                className="card"
                style={{ margin: 0, marginBottom: '1rem' }}
              >
                <div className="card-header">
                  <strong>Identity Services</strong>
                  <div className="row">
                    <button
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      className="ghost"
                      disabled={identityHealthBusy}
                      onClick={() => void onCheckIdentityHealth()}
                    >
                      {identityHealthBusy ? 'Checking...' : 'Test Admin'}
                    </button>
                    {identityHealth && (
                      <span style={{ fontSize: '0.75rem' }}>
                        {!identityHealth.configured && (
                          <span style={{ color: '#dc3545' }}>Not configured</span>
                        )}
                        {identityHealth.configured && identityHealth.reachable === false && (
                          <span style={{ color: '#dc3545' }}>Unreachable</span>
                        )}
                        {identityHealth.configured && identityHealth.auth_ok === false && (
                          <span style={{ color: '#856404' }}>Auth failed</span>
                        )}
                        {identityHealth.auth_ok === true && (
                          <span style={{ color: 'green' }}>Admin OK</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div className="card-body" style={{ padding: '0.5rem' }}>
                  <p style={{ marginBottom: '0.5rem' }}>
                    Configure OAuth/OIDC client credentials before applying deployment files.
                  </p>
                  <table className="table" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {identityServices.map((svc) => {
                        const isComplete = svc.has_client_id && svc.has_client_secret;
                        const needsAction = !isComplete;
                        return (
                          <tr key={svc.service_name}>
                            <td className="mono">{svc.service_name}</td>
                            <td>
                              {isComplete ? (
                                <span style={{ color: 'green' }}>Configured</span>
                              ) : svc.provisioned ? (
                                <span style={{ color: '#856404' }}>Partial</span>
                              ) : (
                                <span style={{ color: '#dc3545' }}>Not provisioned</span>
                              )}
                            </td>
                            <td>
                                <button
                                  className="primary"
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', marginRight: '0.5rem' }}
                                  disabled={identityServicesBusy}
                                  onClick={() => void onReconcileIdentity(svc.service_name)}
                                >
                                  {identityServicesBusy ? 'Working...' : svc.provisioned ? 'Re-reconcile' : 'Auto-Provision'}
                                </button>
                              <ManualIdentityEntry
                                serviceName={svc.service_name}
                                disabled={identityServicesBusy}
                                onSubmit={onManualIdentitySecrets}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {hasMissingSecrets && (
              <div
                className="card"
                style={{ margin: 0, marginBottom: '1rem', backgroundColor: '#fff3cd', borderColor: '#ffc107' }}
              >
                <div className="card-header" style={{ backgroundColor: 'transparent' }}>
                  <strong>Missing Secrets</strong>
                  <div className="row" style={{ gap: '0.5rem' }}>
                    <button
                      className="ghost"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      onClick={() => void onImportDeployedSecrets('stage')}
                      disabled={!preview.stage_id}
                    >
                      Import to Stage
                    </button>
                    <button
                      className="ghost"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      onClick={() => void onImportDeployedSecrets('general')}
                    >
                      Import to General
                    </button>
                    <button
                      className="primary"
                      style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                      onClick={() => void onGenerateMissing()}
                    >
                      Generate All Missing
                    </button>
                  </div>
                </div>
                <div className="card-body" style={{ padding: '0.5rem' }}>
                  <p style={{ marginBottom: '0.5rem' }}>
                    The following secrets need values before deployment can succeed:
                  </p>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                    {missingSecretKeys.map((missingKey) => {
                      const secret = preview.secrets_info.find((item) => item.key === missingKey);
                      const canGenerate = Boolean(secret?.can_generate);
                      const generateType = secret?.generate_type || 'random';
                      return (
                        <li
                          key={missingKey}
                          className="row"
                          style={{ marginBottom: '0.25rem', justifyContent: 'space-between' }}
                        >
                          <span>
                            <span className="mono">{missingKey}</span>
                            {secret?.description && (
                              <span className="hint" style={{ marginLeft: '0.5rem' }}>
                                - {secret.description}
                              </span>
                            )}
                          </span>
                          {canGenerate ? (
                            <button
                              className="primary"
                              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                              onClick={() => void onGenerateSecret(missingKey)}
                            >
                              Generate ({generateType})
                            </button>
                          ) : (
                            <span className="hint" style={{ color: '#856404' }}>
                              Manual setup required
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}

            {preview.secrets_info.length > 0 && (
              <div className="card" style={{ margin: 0, marginBottom: '1rem' }}>
                <div className="card-header">
                  <strong>Secrets ({preview.secrets_info.length})</strong>
                </div>
                <div className="card-body" style={{ padding: '0.5rem' }}>
                  <table className="table" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Status</th>
                        <th>Source</th>
                        <th>File Path</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.secrets_info.map((secret) => (
                        <tr key={secret.key}>
                          <td className="mono">
                            {secret.key}
                            {secret.description && (
                              <span
                                className="hint"
                                style={{ display: 'block', fontSize: '0.7rem' }}
                              >
                                {secret.description}
                              </span>
                            )}
                          </td>
                          <td>
                            {secret.has_value ? (
                              <span style={{ color: 'green' }}>Set</span>
                            ) : secret.materialized ? (
                              <span style={{ color: '#856404' }}>Set (empty)</span>
                            ) : (
                              <span style={{ color: '#dc3545' }}>Missing</span>
                            )}
                          </td>
                          <td>
                            <span
                              style={{
                                color:
                                  secret.source === 'stage_db'
                                    ? 'orange'
                                    : secret.source === 'global_db'
                                      ? 'blue'
                                      : 'gray'
                              }}
                            >
                              {secret.source === 'manifest'
                                ? 'Manifest'
                                : secret.source === 'global_db'
                                  ? 'Global DB'
                                  : secret.source === 'stage_db'
                                    ? 'Stage DB'
                                    : secret.source}
                              {secret.source_type === 'external' && secret.source_detail
                                ? ` (${secret.source_detail})`
                                : secret.source_type === 'external'
                                  ? ' (external)'
                                  : ''}
                            </span>
                          </td>
                          <td className="mono" style={{ fontSize: '0.75rem' }}>
                            {secret.file_path || '-'}
                          </td>
                          <td>
                            {secret.can_generate ? (
                              <button
                                className="ghost"
                                style={{ fontSize: '0.7rem', padding: '0.15rem 0.3rem' }}
                                onClick={() => void onGenerateSecret(secret.key)}
                              >
                                {secret.has_value ? 'Regenerate' : 'Generate'}
                              </button>
                            ) : !secret.has_value && secret.secret_type === 'env' ? (
                              <button
                                className="ghost"
                                style={{ fontSize: '0.7rem', padding: '0.15rem 0.3rem' }}
                                onClick={() => {
                                  const value = window.prompt(`Enter value for ${secret.key}`);
                                  if (value !== null) {
                                    void onSetSecret(secret.key, value);
                                  }
                                }}
                              >
                                Set value
                              </button>
                            ) : null}
                            {(secret.restart_services?.length ?? 0) > 0 && (
                              <span className="hint" style={{ display: 'block' }}>
                                Restarts: {secret.restart_services?.join(', ')}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="hint" style={{ marginTop: '0.5rem' }}>
                    Secrets are written with real values on apply. Global DB secrets apply to all
                    stages.
                  </p>
                </div>
              </div>
            )}

            {preview.external_refs && preview.external_refs.length > 0 && (
              <div className="card" style={{ margin: 0, marginBottom: '1rem' }}>
                <div className="card-header">
                  <strong>External References ({preview.external_refs.length})</strong>
                </div>
                <div className="card-body" style={{ padding: '0.5rem' }}>
                  <p className="hint" style={{ marginBottom: '0.5rem' }}>
                    Non-secret app URL/domain references resolved for this preview. Use them in
                    ops templates as <span className="mono">{'{app:other-app.url}'}</span> or{' '}
                    <span className="mono">{'{app:other-app.same_stage.url}'}</span>.
                  </p>
                  <table className="table" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr>
                        <th>Template key</th>
                        <th>Resolved value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.external_refs.map((ref) => (
                        <tr key={ref.key}>
                          <td className="mono">{`{${ref.key}}`}</td>
                          <td className="mono" style={{ fontSize: '0.75rem' }}>
                            {ref.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="stack">
              <label className="row">
                <input
                  type="checkbox"
                  checked={selectedFiles.size === preview.files.length}
                  onChange={(event) => onToggleAll(event.target.checked)}
                />
                <span>Select all ({preview.files.length} files)</span>
              </label>

              {preview.files.map((file) => {
                const hasDiff =
                  file.current_content !== null && file.current_content !== file.content;
                const isNew = !file.exists;
                const isSelected = selectedFiles.has(file.path);

                return (
                  <div key={file.path} className="card" style={{ margin: 0 }}>
                    <div className="card-header" style={{ padding: '0.5rem' }}>
                      <label className="row">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(event) => onToggleFile(file.path, event.target.checked)}
                        />
                        <span className="mono preview-file-path">{file.path}</span>
                      </label>
                      <div className="row">
                        <span className="hint">{file.file_type}</span>
                        {isNew && <span className="hint" style={{ color: 'green' }}>new</span>}
                        {hasDiff && (
                          <span className="hint" style={{ color: 'orange' }}>modified</span>
                        )}
                        {!isNew && !hasDiff && <span className="hint">unchanged</span>}
                      </div>
                    </div>
                    <details style={{ marginLeft: '1.5rem' }}>
                      <summary className="hint">View content</summary>
                      <div className="stack" style={{ marginTop: '0.5rem' }}>
                        {hasDiff && (
                          <div className="stack">
                            <span className="hint">Current (on server):</span>
                            <pre
                              className="log-box"
                              style={{ fontSize: '0.75rem', maxHeight: '150px', overflow: 'auto' }}
                            >
                              {file.current_content || '(empty)'}
                            </pre>
                          </div>
                        )}
                        <div className="stack">
                          <span className="hint">
                            {isNew ? 'New content:' : 'Updated content:'}
                          </span>
                          <pre
                            className="log-box"
                            style={{ fontSize: '0.75rem', maxHeight: '150px', overflow: 'auto' }}
                          >
                            {file.content}
                          </pre>
                        </div>
                      </div>
                    </details>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="dialog-footer">
          <div className="row" style={{ justifyContent: 'space-between', width: '100%' }}>
            <span className="hint">
              {selectedFiles.size} of {preview.files.length} files selected
            </span>
            {hasMissingSecrets && (
              <span className="hint" style={{ color: '#dc3545' }}>
                Resolve {missingSecretKeys.length} missing secret(s) before apply.
              </span>
            )}
            <div className="row">
              <button type="button" className="secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void onApply()}
                disabled={applying || selectedFiles.size === 0 || hasMissingSecrets}
              >
                {applying ? 'Applying...' : `Apply ${selectedFiles.size} Files`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type OpsStageSnapshot = {
  name: string;
  isDefault: boolean;
  slugSuffix: string;
  gitlabEnvironmentName: string | null;
  dnsRecord: string | null;
  containers: string[];
};

export function AppDetailOperationsView({
  deployResult,
  pipelineError,
  pipelines,
  pipelineJobsOpen,
  pipelineJobsLoading,
  pipelineJobs,
  deployWaitBusyJobs,
  redeployBusyId,
  onRefreshPipelines,
  onTogglePipelineJobs,
  onRedeployPipeline,
  isPipelineFinished,
  isManualJob,
  isPendingJob,
  deployWaitJobKey,
  onCancelDeployWait,
  onArmDeployWait,
  onLoadMorePipelines,
  unifiedPreviewBusy,
  onLoadUnifiedPreview,
  stageTabs,
  selectedOpsStageId,
  onSelectOpsStageId,
  opsStageSnapshot,
  opsOverview,
  opsResult,
  opsHistory,
  opsHistoryBusy,
  opsHistoryResult,
  opsRevertBusyCommit,
  onRefreshOpsHistory,
  onRevertOpsCommit,
  gitlabRefreshBusy,
  onRecalculateGitlabStatus,
  onLoadConversations,
  apiClient,
  availableApps,
  appId,
  onConversationCreated,
  setError,
  conversations,
  onDeleteConversation
}: {
  deployResult: string | null;
  pipelineError: string | null;
  pipelines: Pipeline[];
  pipelineJobsOpen: Record<number, boolean>;
  pipelineJobsLoading: Record<number, boolean>;
  pipelineJobs: Record<number, PipelineJob[]>;
  deployWaitBusyJobs: Record<string, boolean>;
  redeployBusyId: number | null;
  onRefreshPipelines: () => void;
  onTogglePipelineJobs: (pipelineId: number) => void;
  onRedeployPipeline: (pipelineId: number) => void;
  isPipelineFinished: (status: string) => boolean;
  isManualJob: (job: PipelineJob) => boolean;
  isPendingJob: (job: PipelineJob) => boolean;
  deployWaitJobKey: (pipelineId: number, jobId: number) => string;
  onCancelDeployWait: (pipelineId: number, job: PipelineJob) => void;
  onArmDeployWait: (pipeline: Pipeline, job: PipelineJob) => void;
  onLoadMorePipelines: () => void;
  unifiedPreviewBusy: boolean;
  onLoadUnifiedPreview: () => void;
  stageTabs: { id: string; label: string }[];
  selectedOpsStageId: string;
  onSelectOpsStageId: (id: string) => void;
  opsStageSnapshot: OpsStageSnapshot;
  opsOverview: OpsOverview | null;
  opsResult: string | null;
  opsHistory: OpsHistoryResponse | null;
  opsHistoryBusy: boolean;
  opsHistoryResult: string | null;
  opsRevertBusyCommit: string | null;
  onRefreshOpsHistory: () => void;
  onRevertOpsCommit: (commit: string) => void;
  gitlabRefreshBusy: boolean;
  onRecalculateGitlabStatus: () => void;
  onLoadConversations: () => void;
  apiClient: ApiClient;
  availableApps: AppItem[];
  appId: string;
  onConversationCreated: (conversationId: string) => void;
  setError: (message: string | null) => void;
  conversations: Conversation[];
  onDeleteConversation: (conversationId: string) => void;
}) {
  return (
    <>
      <div className="card">
        <div className="card-header">
          <h3>Build pipelines</h3>
          <div className="row">
            <button onClick={onRefreshPipelines}>Refresh</button>
          </div>
        </div>
        {deployResult && <p className="status">{deployResult}</p>}
        {pipelineError && <p className="status warning">{pipelineError}</p>}
        <ul className="list">
          {pipelines.map((pipeline) => (
            <li key={pipeline.id}>
              <a href={pipeline.web_url} target="_blank" rel="noreferrer">
                {pipeline.commit_title ||
                  pipeline.commit_message?.split('\n')[0] ||
                  pipeline.commit_short_id ||
                  pipeline.sha?.slice(0, 8) ||
                  `#${pipeline.id}`}
              </a>
              <div className="row">
                <span>{pipeline.status}</span>
                <span>{pipeline.ref}</span>
                <button
                  type="button"
                  className="ghost compact"
                  onClick={() => onTogglePipelineJobs(pipeline.id)}
                >
                  {pipelineJobsOpen[pipeline.id] ? 'Hide jobs' : 'Show jobs'}
                </button>
                {isPipelineFinished(pipeline.status) && (
                  <button
                    type="button"
                    className="ghost compact"
                    onClick={() => onRedeployPipeline(pipeline.id)}
                    disabled={redeployBusyId === pipeline.id}
                    title="Redeploy"
                  >
                    {redeployBusyId === pipeline.id ? 'Redeploying...' : 'Redeploy'}
                  </button>
                )}
              </div>
              {pipelineJobsOpen[pipeline.id] && (
                <div className="pipeline-jobs">
                  {pipelineJobsLoading[pipeline.id] && <div className="status">Loading jobs…</div>}
                  {pipelineJobs[pipeline.id] && pipelineJobs[pipeline.id].length > 0 ? (
                    (() => {
                      const jobs = pipelineJobs[pipeline.id];
                      const candidateJobs = jobs.filter(
                        (job) => isManualJob(job) || isPendingJob(job)
                      );
                      if (candidateJobs.length === 0) {
                        return (
                          <div className="status">
                            No manual or pending jobs in this pipeline yet.
                          </div>
                        );
                      }
                      const waits = pipeline.deploy_waits ?? [];
                      const stages = new Map<string, PipelineJob[]>();
                      for (const job of candidateJobs) {
                        const stage = job.stage ?? 'default';
                        const list = stages.get(stage) ?? [];
                        list.push(job);
                        stages.set(stage, list);
                      }
                      return (
                        <>
                          {[...stages.entries()].map(([stage, stageJobs]) => (
                            <div className="pipeline-stage" key={stage}>
                              <span className="stage-label">{stage}</span>
                              <div className="job-bubbles">
                                {stageJobs.map((job) => {
                                  const waitMatch = waits.find((wait) => wait.job_id === job.id);
                                  const isArmed = Boolean(waitMatch);
                                  const isManualReady = job.status.toLowerCase() === 'manual';
                                  const busyKey = deployWaitJobKey(pipeline.id, job.id);
                                  return (
                                    <div
                                      key={job.id}
                                      className={`job-bubble job-bubble--${job.status}`}
                                    >
                                      <span>{job.name}</span>
                                      <span className="badge">{job.status}</span>
                                      {job.when && job.when !== 'manual' && (
                                        <span className="hint">when: {job.when}</span>
                                      )}
                                      {isArmed ? (
                                        <button
                                          type="button"
                                          className="secondary compact"
                                          onClick={() => onCancelDeployWait(pipeline.id, job)}
                                          disabled={deployWaitBusyJobs[busyKey]}
                                        >
                                          {deployWaitBusyJobs[busyKey] ? 'Aborting...' : 'Abort'}
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          className="ghost compact"
                                          onClick={() => onArmDeployWait(pipeline, job)}
                                          disabled={deployWaitBusyJobs[busyKey]}
                                        >
                                          {deployWaitBusyJobs[busyKey]
                                            ? isManualReady
                                              ? 'Deploying...'
                                              : 'Arming...'
                                            : isManualReady
                                              ? 'Deploy'
                                              : 'Arm'}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </>
                      );
                    })()
                  ) : (
                    <div className="status">No jobs loaded yet.</div>
                  )}
                </div>
              )}
            </li>
          ))}
          {pipelines.length === 0 && <li>No pipelines found.</li>}
        </ul>
        <div className="row">
          <button type="button" className="ghost" onClick={onLoadMorePipelines}>
            Load more
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Deploy Preview</h3>
          <div className="row">
            <button onClick={onLoadUnifiedPreview} disabled={unifiedPreviewBusy}>
              {unifiedPreviewBusy ? 'Loading...' : 'Preview all deployment files'}
            </button>
          </div>
        </div>
        <StageTabs tabs={stageTabs} selectedId={selectedOpsStageId} onSelect={onSelectOpsStageId} />
        <div className="stack">
          <div className="row">
            <span>Stage</span>
            <span className="mono">{opsStageSnapshot.name}</span>
            {opsStageSnapshot.isDefault && <span className="badge">Default</span>}
          </div>
          <div className="row">
            <span>Slug suffix</span>
            <span className="mono">{opsStageSnapshot.slugSuffix || '—'}</span>
          </div>
          <div className="row">
            <span>GitLab environment</span>
            <span className="mono">
              {opsStageSnapshot.gitlabEnvironmentName ?? 'Not enabled for generated CI'}
            </span>
          </div>
          <div className="row">
            <span>DNS</span>
            <span className="mono">{opsStageSnapshot.dnsRecord ?? '—'}</span>
          </div>
          <div className="row">
            <span>Containers</span>
            <span className="mono">{opsStageSnapshot.containers.join(', ') || '—'}</span>
          </div>
          {opsOverview?.warnings && opsOverview.warnings.length > 0 && (
            <p className="status warning">
              <strong>Warnings:</strong> {opsOverview.warnings.join(' · ')}
            </p>
          )}
          {!opsOverview || !opsOverview.present ? (
            <p className="status">
              {opsOverview?.missing_reason || 'No deploy/ops.yaml found for this app.'}
            </p>
          ) : (
            <p className="status">
              Use unified preview to review scripts, config templates, and secret files in one
              apply flow for this stage.
            </p>
          )}
          {opsResult && <p className="status">{opsResult}</p>}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Config History</h3>
          <div className="row">
            <button type="button" className="ghost" onClick={onRefreshOpsHistory} disabled={opsHistoryBusy}>
              {opsHistoryBusy ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>
        <StageTabs tabs={stageTabs} selectedId={selectedOpsStageId} onSelect={onSelectOpsStageId} />
        <div className="stack">
          <div className="row">
            <span>Repo</span>
            <span className="mono">{opsHistory?.repo_path ?? '—'}</span>
          </div>
          <div className="row">
            <span>Current commit</span>
            <span className="mono">{opsHistory?.current_commit?.slice(0, 8) ?? '—'}</span>
          </div>
          {opsHistoryResult && (
            <p
              className={`status${
                opsHistoryResult.toLowerCase().includes('unavailable') ? ' warning' : ''
              }`}
            >
              {opsHistoryResult}
            </p>
          )}
          {!opsHistory || !opsHistory.repository_initialized ? (
            <p className="status">
              Config history becomes available after the first tracked ops apply for this stage.
            </p>
          ) : opsHistory.commits.length === 0 ? (
            <p className="status">No tracked config commits found yet.</p>
          ) : (
            <ul className="list">
              {opsHistory.commits.map((entry) => {
                const isCurrent = opsHistory.current_commit === entry.commit;
                const busy = opsRevertBusyCommit === entry.commit;
                return (
                  <li key={entry.commit}>
                    <div className="stack" style={{ gap: 8 }}>
                      <div className="row">
                        <span className="mono">{entry.short_commit}</span>
                        {isCurrent && <span className="badge">Current</span>}
                        <span>{new Date(entry.committed_at).toLocaleString()}</span>
                      </div>
                      <div>{entry.subject}</div>
                      <div className="row">
                        <button
                          type="button"
                          className="ghost compact"
                          disabled={busy || isCurrent}
                          onClick={() => onRevertOpsCommit(entry.commit)}
                        >
                          {busy ? 'Restoring…' : isCurrent ? 'Current state' : 'Restore to this commit'}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="card full">
        <div className="card-header">
          <h3>Conversations</h3>
          <div className="row">
            <button
              className="ghost"
              onClick={onRecalculateGitlabStatus}
              disabled={gitlabRefreshBusy}
            >
              {gitlabRefreshBusy ? 'Recalculating…' : 'Recalculate GitLab'}
            </button>
            <button onClick={onLoadConversations}>Refresh</button>
          </div>
        </div>
        <ConversationCreateForm
          apiClient={apiClient}
          apps={availableApps}
          baseAppId={appId}
          allowNoApp={false}
          allowAppSelect={false}
          onCreated={(created) => onConversationCreated(created.id)}
          setError={setError}
        />
        <div className="hint">
          Need a no-app conversation? Start one from the global Conversations page.
        </div>
        <ConversationTable
          conversations={conversations}
          showAppColumn={false}
          linkFor={(conversation) => `/conversations/${conversation.id}`}
          onDelete={onDeleteConversation}
        />
      </div>
    </>
  );
}

function ManualIdentityEntry({
  serviceName,
  disabled,
  onSubmit
}: {
  serviceName: string;
  disabled: boolean;
  onSubmit: (serviceName: string, clientId: string, clientSecret: string) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  if (!open) {
    return (
      <button
        style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
        className="ghost"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        Enter manually
      </button>
    );
  }

  return (
    <div style={{ display: 'inline-flex', gap: '0.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        type="text"
        placeholder="Client ID"
        value={clientId}
        onChange={(e) => setClientId(e.target.value)}
        style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem', width: '120px' }}
        autoComplete="off"
      />
      <input
        type="password"
        placeholder="Client Secret"
        value={clientSecret}
        onChange={(e) => setClientSecret(e.target.value)}
        style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem', width: '120px' }}
        autoComplete="new-password"
      />
      <button
        style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem' }}
        disabled={disabled || !clientId.trim() || !clientSecret.trim()}
        onClick={() => {
          void onSubmit(serviceName, clientId.trim(), clientSecret.trim());
          setOpen(false);
          setClientId('');
          setClientSecret('');
        }}
      >
        Save
      </button>
      <button
        style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem' }}
        className="ghost"
        onClick={() => {
          setOpen(false);
          setClientId('');
          setClientSecret('');
        }}
      >
        Cancel
      </button>
    </div>
  );
}
